import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter, inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {ReactiveFormsModule, UntypedFormGroup} from '@angular/forms';
import {BehaviorSubject, combineLatestWith, debounceTime, Observable, of, startWith, tap} from 'rxjs';
import {ContentView, InputContentViewComponent} from '../input-content-view/input-content-view.component';
import {map} from 'rxjs/operators';
import { EntityInputComponent } from '@softline/ui-core/entity-input';
import {FocusMonitor} from '@angular/cdk/a11y';
import {CommonModule} from '@angular/common';
import {ScrollIntoViewDirective} from '../../directives/scroll-into-view.directive';
import {isDefined, Store} from "@softline/core";
import {
  HardwareListeEntityInputParams,
  HardwareListeEntityInputStrategy
} from '../../strategies/hardware-liste-entity-input.strategy';

@Component({
    selector: 'lib-input-base',
    imports: [CommonModule, ReactiveFormsModule, ScrollIntoViewDirective, InputContentViewComponent,
    EntityInputComponent],
    templateUrl: './input-base.component.html',
    styleUrls: ['./input-base.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class InputBaseComponent<Entity extends { id: string | number }> implements OnInit {

  @ViewChild('entityInput', { read: ElementRef })
  private entityInput?: ElementRef<HTMLElement>;

  @Input() required = false;

  @Input()
  set editing(value: Entity | undefined | null) {
    this.entityInEditing$.next(value);

    if (value) {
      this.selectedView$.next('edit');
    } else {
      this.selectedView$.next('results');
    }
  }

  @Input()
  get entities(): Entity[] { return this.entities$.value; }
  set entities(value: Entity[]) {
    this.entities$.next(value);
  }

  @ViewChild('emptyTemplate', { static: true, read: TemplateRef }) private emptyTemplate!: TemplateRef<any>;

  private readonly entities$ = new BehaviorSubject<Entity[]>([]);

  readonly selectedView$ = new BehaviorSubject<ContentView['id']>('results');
  readonly entityInEditing$ = new BehaviorSubject<Entity | null | undefined>(null);
  readonly highlightedIndex$ = new BehaviorSubject(-1);

  readonly isOpen$ = new BehaviorSubject<boolean>(false);
  readonly input$ = new BehaviorSubject<string | null>('');

  private readonly lowercasedDebouncedInput$ = this.input$.pipe(
    map(o => !!o ? o : ''),
    map(o => o?.toLowerCase()),
    tap(input => {
      if (input && input?.length >= 1) {
        this.isOpen$.next(true);
      }
    }),
    debounceTime(300),
  );

  // tslint:disable-next-line:no-output-native
  @Output() readonly select = new EventEmitter<Entity | null | undefined>();
  @Output() readonly edit = new EventEmitter<Entity | null| undefined>();
  @Output() readonly formReset = new EventEmitter<Entity | null | undefined>();
  @Output() readonly valueChange = new EventEmitter<Entity | null | undefined>();

  @Input() formGroup!: UntypedFormGroup;
  @Input() controlName!: string;
  @Input() label = 'Entity';
  @Input() readonly = false;

  //@Input() entityEditTemplate!: TemplateRef<{ $implicit: Entity; }>;

  @Input()
  get entityCreateTemplate(): TemplateRef<{  $implicit: Entity }> {
    return this.entityCreateTemplate$.value;
  }
  set entityCreateTemplate(value: TemplateRef<{ $implicit: Entity; }>) {
      console.log('Entity create template: ', value);
      this.entityCreateTemplate$.next(value);
  }

  @Input()
  get entityResultTemplate(): TemplateRef<{ $implicit: Entity; highlighted: boolean }> {
    return this.entityResultTemplate$.value;
  }
  set entityResultTemplate(value: TemplateRef<{ $implicit: Entity; highlighted: boolean }>) {
    this.entityResultTemplate$.next(value);
  }

  @Input() selectedTemplate?: TemplateRef<{ $implicit: Entity; entity: Entity; }>;

  get entityEditTemplate(): TemplateRef<any> {
    return this.entityEditTemplate$.value ?? this.emptyTemplate;
  }

  @Input()
  set entityEditTemplate(value: TemplateRef<any> | null) {
    this.entityEditTemplate$.next(value ?? this.emptyTemplate);
  }

  value$: Observable<Entity | null> = of(null);

  @Input() filter: (entities: Entity[], filter: string) => Entity[] = (entities) => entities;

  @Input()
  set clearInputTrigger(value: unknown) {
    this.input$.next('');
  }

  readonly entityCreateTemplate$ = new BehaviorSubject<TemplateRef<any>>(this.emptyTemplate);
  readonly entityResultTemplate$ = new BehaviorSubject<TemplateRef<any>>(this.emptyTemplate);
  readonly entityEditTemplate$ = new BehaviorSubject<TemplateRef<any>>(this.emptyTemplate);

  readonly filteredEntities$ = this.entities$.pipe(
    combineLatestWith(
      this.lowercasedDebouncedInput$,
      this.isOpen$,
      this.value$.pipe(startWith(null))
    ),
    map(([entities, filter]) => this.filter(entities as Entity[], filter))
  );

  readonly views: ContentView[] = [
    {
      id: 'results',
      label: 'Ergebnis',
      visible$: of(true)
    },
    {
      id: 'edit',
      label: 'Bearbeiten',
      visible$: this.entityInEditing$.pipe(
        map(o => !!o)
      )
    },
    {
      id: 'create',
      label: 'Erstellen',
      visible$: this.entityEditTemplate$.pipe(map(o => isDefined(o)))
    }
  ];

  private params: HardwareListeEntityInputParams = {
    highlightedIndex$: this.highlightedIndex$,
    selectedView$: this.selectedView$,
    entityInEditing$: this.entityInEditing$,
    input$: this.input$,
    entities$: this.entities$,
    filteredEntities$: this.filteredEntities$,
    entityCreateTemplate$: this.entityCreateTemplate$,
    entityResultTemplate$: this.entityResultTemplate$,
    entityEditTemplate$: this.entityEditTemplate$ } as unknown as HardwareListeEntityInputParams;

  readonly strategy = new HardwareListeEntityInputStrategy(
    inject(Store),
    this.params
  );

  constructor(
    private store: Store,
    private focusMonitor: FocusMonitor
  ) {}

  ngOnInit() {
    this.value$ = this.formGroup.valueChanges.pipe(
      map(o => o[this.controlName] ?? null)
    );
  }

  onValidate(value: string): void {
    if (!value || value?.length < 1) {
      this.input$.next('');
      return;
    }

    const input = this.input$.value?.toLowerCase() ?? '';
    const results = this.filter(this.entities, input);

    if (results?.length === 1) {
      this.onSelect(results[0]);
    } else if (results?.length > 1) {
      this.selectedView$.next('results');
      this.isOpen$.next(true);
    } else {
      this.selectedView$.next('create');
      this.isOpen$.next(true);
    }
  }

  onSelect(entity: Entity): void {
    this.select.emit(entity);
    this.input$.next('');
  }

  resetForm(entity?: Entity): void {
    if (!entity) {
      this.formReset.emit();
      return;
    }

    this.formReset.emit(entity);
  }

  isOpenChanged(newValue: boolean): void {
    this.isOpen$.next(newValue);
    this.highlightedIndex$.next(-1);
    this.formReset.emit();
    this.inputChanged('');

    if (newValue && this.entityInput && this.entityInput?.nativeElement) {
      this.focusMonitor.focusVia(this.entityInput.nativeElement.children.item(0) as HTMLElement, 'mouse');
    }
  }

  valueChanged(newValue: Entity | null): void {
    // if the value got cleared/deleted we have to reset the filter
    if (!newValue) {
      this.input$.next('');
      this.highlightedIndex$.next(-1);
      this.formReset.emit();
      this.entityInEditing$.next(null);
    }

    this.isOpen$.next(false);
    this.valueChange.emit(newValue);
  }

  canceled(entityInput?: EntityInputComponent<Entity, unknown>): void {
    this.input$.next('');

    this.formGroup.patchValue({ produkt: null });
    this.entityInEditing$.next(null);
    this.isOpen$.next(false);
    this.formReset.emit();

    if (entityInput) {
      // remove any existing text of the input (reset)
      entityInput.onInput('');
      entityInput.input = '';
      entityInput.inputComponent.nativeElement.value = '';
    }
  }

  closed(): void {
    this.input$.next('');
    this.isOpen$.next(false);
    this.formReset.emit();
  }

  inputChanged(input: string): void {
    this.input$.next(input);
  }

  highlightPrevious(): void {
    if (this.highlightedIndex$.value === 0) {
      return;
    }

    const next = this.highlightedIndex$.value - 1;
    this.highlightedIndex$.next(next);
  }

  highlightNext(): void {
    if (this.highlightedIndex$.value >= this.entities.length - 1) {
      return;
    }

    const next = this.highlightedIndex$.value + 1;
    this.highlightedIndex$.next(next);
  }

  selectHighlightedProduct(): void {
    const index = this.highlightedIndex$.value;

    const input = this.input$.value?.toLowerCase() ?? '';
    const results = this.filter(this.entities, input);

    if (index < 0) {
      return;
    }

    this.onSelect(results[index]);
  }

  trackByFn(_: number, entity: Entity): number | string {
    return entity.id;
  }
}
